"use client";

import { useEffect, useState, type FormEvent } from "react";

type Program = {
  id: number;
  name?: string;
  env?: string;
  tenantCode?: string;
};

function programLabel(program: Program) {
  const name = program.name ?? `项目 ${program.id}`;
  const extra = [program.env, program.tenantCode].filter(Boolean).join(" · ");
  return extra ? `${name} · ${extra}` : name;
}

export function DataApiConsole() {
  const [programs, setPrograms] = useState<Program[]>([]);
  const [programId, setProgramId] = useState<number | "">("");
  const [yaql, setYaql] = useState("");
  const [status, setStatus] = useState("正在读取项目…");
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [querying, setQuerying] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadPrograms() {
      try {
        const response = await fetch("/api/programs");
        const payload: unknown = await response.json();
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          const message =
            payload && typeof payload === "object" && "error" in payload
              ? String((payload as { error: unknown }).error)
              : "读取项目失败";
          setError(message);
          setStatus("");
          return;
        }
        if (!Array.isArray(payload)) {
          setError("项目列表格式无法识别");
          setStatus("");
          return;
        }
        const list = payload.filter(
          (item): item is Program =>
            Boolean(item) && typeof item === "object" && typeof (item as Program).id === "number",
        );
        setPrograms(list);
        setProgramId(list[0]?.id ?? "");
        setStatus(list.length === 0 ? "服务已连接，但没有返回项目。" : "");
      } catch {
        if (!cancelled) {
          setError("无法连接本地 DataAPI 服务（127.0.0.1:8080）");
          setStatus("");
        }
      }
    }

    void loadPrograms();
    return () => {
      cancelled = true;
    };
  }, []);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setResult("");
    if (programId === "") {
      setError("请选择项目");
      return;
    }
    if (yaql.trim() === "") {
      setError("请输入 YAQL");
      return;
    }

    setQuerying(true);
    try {
      const response = await fetch("/api/dataapi", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ programId, yaql }),
      });
      const text = await response.text();
      if (!response.ok) {
        try {
          const payload = JSON.parse(text) as { error?: string };
          setError(payload.error ?? "查询失败");
        } catch {
          setError(text || "查询失败");
        }
        return;
      }
      try {
        setResult(JSON.stringify(JSON.parse(text), null, 2));
      } catch {
        setResult(text);
      }
    } catch {
      setError("查询请求没有完成");
    } finally {
      setQuerying(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="rounded-[28px] bg-white p-7 md:p-10">
      <label htmlFor="program" className="block text-sm text-muted">
        项目
      </label>
      <select
        id="program"
        value={programId}
        onChange={(event) => setProgramId(event.target.value === "" ? "" : Number(event.target.value))}
        disabled={programs.length === 0}
        className="mt-2 h-11 w-full rounded-xl bg-canvas px-4 text-sm outline-none focus:ring-2 focus:ring-blue disabled:text-soft"
      >
        {programs.length === 0 ? <option value="">暂无项目</option> : null}
        {programs.map((program) => (
          <option key={program.id} value={program.id}>
            {programLabel(program)}
          </option>
        ))}
      </select>

      <label htmlFor="yaql" className="mt-6 block text-sm text-muted">
        YAQL
      </label>
      <textarea
        id="yaql"
        value={yaql}
        onChange={(event) => setYaql(event.target.value)}
        rows={8}
        className="mt-2 w-full resize-y rounded-xl bg-canvas px-4 py-3 font-mono text-sm leading-6 outline-none focus:ring-2 focus:ring-blue"
        placeholder="输入要执行的 YAQL"
      />

      <div className="mt-6 flex items-center gap-4">
        <button
          type="submit"
          disabled={querying || programs.length === 0}
          className="h-9 rounded-full bg-blue px-5 text-sm text-white disabled:opacity-40"
        >
          {querying ? "查询中…" : "查询"}
        </button>
        {status ? <p className="text-sm text-muted">{status}</p> : null}
      </div>

      {error ? <p className="mt-6 text-sm text-[#e30000]">{error}</p> : null}
      {result ? (
        <pre className="mt-6 overflow-auto rounded-2xl bg-black p-5 text-sm leading-6 text-white">{result}</pre>
      ) : null}
    </form>
  );
}
